import TableView, { TableSection } from '@/components/TableView'
import { getCameraPermissionsAsync, requestCameraPermissionsAsync } from 'expo-image-picker'
import { getRecordingPermissionsAsync, requestRecordingPermissionsAsync } from 'expo-audio'
import * as Location from 'expo-location'
import * as MediaLibrary from 'expo-media-library/legacy'
import { useFocusEffect } from 'expo-router'
import { useCallback, useState } from 'react'
import { Linking } from 'react-native'

type PermissionStatus = 'granted' | 'denied' | 'undetermined'

type PermissionsState = {
  camera: PermissionStatus
  mediaLibrary: PermissionStatus
  microphone: PermissionStatus
  location: PermissionStatus
}

function getStatusText(status: PermissionStatus): string {
  switch (status) {
    case 'granted':
      return '已授权'
    case 'denied':
      return '未授权'
    case 'undetermined':
      return '未授权'
  }
}

export default function Permissions() {
  const [permissions, setPermissions] = useState<PermissionsState>({
    camera: 'undetermined',
    mediaLibrary: 'undetermined',
    microphone: 'undetermined',
    location: 'undetermined',
  })

  const checkPermissions = useCallback(async () => {
    const [cameraResult, mediaLibraryResult, microphoneResult, locationResult] = await Promise.all([
      getCameraPermissionsAsync(),
      MediaLibrary.getPermissionsAsync(),
      getRecordingPermissionsAsync(),
      Location.getForegroundPermissionsAsync(),
    ])

    setPermissions({
      camera: cameraResult.granted ? 'granted' : cameraResult.canAskAgain ? 'undetermined' : 'denied',
      mediaLibrary: mediaLibraryResult.accessPrivileges === 'all'
        ? 'granted'
        : mediaLibraryResult.accessPrivileges === 'limited'
          ? 'granted'
          : mediaLibraryResult.canAskAgain
            ? 'undetermined'
            : 'denied',
      microphone: microphoneResult.granted ? 'granted' : microphoneResult.canAskAgain ? 'undetermined' : 'denied',
      location: locationResult.granted ? 'granted' : locationResult.canAskAgain ? 'undetermined' : 'denied',
    })
  }, [])

  useFocusEffect(
    useCallback(() => {
      checkPermissions()
    }, [checkPermissions])
  )

  const handleCameraPress = async () => {
    if (permissions.camera === 'undetermined') {
      const result = await requestCameraPermissionsAsync()
      setPermissions(prev => ({
        ...prev,
        camera: result.granted ? 'granted' : result.canAskAgain ? 'undetermined' : 'denied',
      }))
    } else if (permissions.camera === 'denied') {
      Linking.openSettings()
    }
  }

  const handleMediaLibraryPress = async () => {
    if (permissions.mediaLibrary === 'undetermined') {
      const result = await MediaLibrary.requestPermissionsAsync()
      setPermissions(prev => ({
        ...prev,
        mediaLibrary: result.accessPrivileges === 'all' || result.accessPrivileges === 'limited'
          ? 'granted'
          : result.canAskAgain
            ? 'undetermined'
            : 'denied',
      }))
    } else if (permissions.mediaLibrary === 'denied') {
      Linking.openSettings()
    }
  }

  const handleMicrophonePress = async () => {
    if (permissions.microphone === 'undetermined') {
      const result = await requestRecordingPermissionsAsync()
      setPermissions(prev => ({
        ...prev,
        microphone: result.granted ? 'granted' : result.canAskAgain ? 'undetermined' : 'denied',
      }))
    } else if (permissions.microphone === 'denied') {
      Linking.openSettings()
    }
  }

  const handleLocationPress = async () => {
    if (permissions.location === 'undetermined') {
      const result = await Location.requestForegroundPermissionsAsync()
      setPermissions(prev => ({
        ...prev,
        location: result.granted ? 'granted' : result.canAskAgain ? 'undetermined' : 'denied',
      }))
    } else if (permissions.location === 'denied') {
      Linking.openSettings()
    }
  }

  const handleNotificationsPress = () => {
    Linking.openSettings()
  }

  const sections: TableSection[] = [
    {
      title: '权限管理',
      rows: [
        {
          icon: 'camera',
          title: '相机',
          detailText: getStatusText(permissions.camera),
          onPress: permissions.camera !== 'granted' ? handleCameraPress : undefined,
        },
        {
          icon: 'photo.on.rectangle',
          title: '照片',
          detailText: getStatusText(permissions.mediaLibrary),
          onPress: permissions.mediaLibrary !== 'granted' ? handleMediaLibraryPress : undefined,
        },
        {
          icon: 'mic',
          title: '麦克风',
          detailText: getStatusText(permissions.microphone),
          onPress: permissions.microphone !== 'granted' ? handleMicrophonePress : undefined,
        },
        {
          icon: 'location',
          title: '位置',
          detailText: getStatusText(permissions.location),
          onPress: permissions.location !== 'granted' ? handleLocationPress : undefined,
        },
        {
          icon: 'bell',
          title: '通知',
          detailText: '前往设置',
          onPress: handleNotificationsPress,
        },
      ],
    },
  ]

  return <TableView sections={sections} />
}
